/**
 * TimingServiceModal.jsx — iter197l (Feb 2026)
 *
 * Guided 4-step workflow for FOH to construct a Timing Service Job Pack.
 * Spec: Yestimate Build Spec — Timing Service Intelligence Library.
 *
 * Steps:
 *   1. Service Type    — pick from 8 seed templates (Timing Belt, Wet Belt, Chain, etc.)
 *   2. Vehicle Intel   — auto-detect engine_code from job, FOH can override
 *   3. Build Pack      — mandatory pre-ticked, recommended checkboxes, risks visible
 *   4. Review & Apply  — confidence badges, customer warning preview, FOH labour adjust
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertTriangle, ShieldAlert, Wrench, CheckCircle2, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const CONFIDENCE_COLORS = {
  high:   "bg-emerald-100 text-emerald-800 border-emerald-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  low:    "bg-red-100 text-red-800 border-red-300",
};
const RISK_LEVEL_COLORS = {
  low:    "bg-slate-100 text-slate-700 border-slate-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  high:   "bg-red-100 text-red-800 border-red-300",
};

export default function TimingServiceModal({ open, onClose, job, onApplied }) {
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [coverTypes, setCoverTypes] = useState([]);
  const [serviceType, setServiceType] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [manufacturer, setManufacturer] = useState("");
  const [engineCode, setEngineCode] = useState("");
  const [displacement, setDisplacement] = useState("");
  const [coverType, setCoverType] = useState(null);
  const [resolvedPack, setResolvedPack] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [labourOverride, setLabourOverride] = useState("");
  const [integrity, setIntegrity] = useState(null);
  const [busy, setBusy] = useState(false);

  // Load templates on open + seed vehicle data from job.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/timing-services/templates`);
        setTemplates(data.templates || []);
        setCoverTypes(data.cover_types || []);
      } catch (e) {
        toast.error("Failed to load timing templates");
      }
    })();
    if (job) {
      setManufacturer(job.make || job.vehicle?.make || "");
      setEngineCode(job.engine_code || job.vehicle?.engine_code || "");
      // displacement may arrive as litres (job.vehicle.displacement) or cc
      // (job.engine_capacity from the DVSA payload — 998 = 1.0L).
      const cc = job.engine_capacity || job.vehicle?.engine_capacity;
      const litres = job.vehicle?.displacement || job.engine_displacement;
      const disp = litres || (cc ? (Number(cc) / 1000).toFixed(1) : "");
      setDisplacement(String(disp || ""));
    }
    setStep(1);
    setServiceType(null);
    setTemplateId(null);
    setCoverType(null);
    setResolvedPack(null);
    setSelected(new Set());
    setLabourOverride("");
    setIntegrity(null);
  }, [open, job]);

  // Group templates by service type for the picker.
  const templatesByService = useMemo(() => {
    const grouped = {};
    for (const t of templates) {
      grouped[t.service_type] ||= [];
      grouped[t.service_type].push(t);
    }
    return grouped;
  }, [templates]);

  const SERVICE_LABELS = {
    timing_belt:     { label: "Timing Belt",     icon: Wrench, color: "text-blue-600" },
    wet_belt:        { label: "Wet Belt",        icon: AlertTriangle, color: "text-orange-600" },
    timing_chain:    { label: "Timing Chain",    icon: Wrench, color: "text-indigo-600" },
    auxiliary_belt:  { label: "Auxiliary Belt",  icon: Wrench, color: "text-slate-600" },
  };

  // --- Step 1 → Step 2 (vehicle intel + pack build) ---------------------
  const goStep2 = (tplId) => {
    const tpl = templates.find((t) => t.id === tplId);
    setTemplateId(tplId);
    setServiceType(tpl?.service_type);
    setCoverType(tpl?.default_cover_type);
    setStep(2);
  };

  // --- Step 2 → Step 3 (build + show ops) -------------------------------
  const goStep3 = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/timing-services/build`, {
        service_type: serviceType,
        template_id: templateId,
        manufacturer: manufacturer || null,
        engine_code: engineCode || null,
        displacement: displacement ? Number(displacement) : null,
        cover_type: coverType,
      });
      setResolvedPack(data);
      // Pre-tick mandatory + every recommended op (spec: protect against omissions).
      const init = new Set();
      (data.mandatory_ops || []).forEach((o) => init.add(o.id));
      (data.recommended_ops || []).forEach((o) => init.add(o.id));
      setSelected(init);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to build pack");
    } finally {
      setBusy(false);
    }
  };

  // --- Step 3 → Step 4 (integrity check + review) -----------------------
  const goStep4 = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/timing-services/check-integrity`, {
        template_id: templateId,
        selected_op_ids: Array.from(selected),
      });
      setIntegrity(data);
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Integrity check failed");
    } finally {
      setBusy(false);
    }
  };

  // --- Step 4 → Apply --------------------------------------------------
  const apply = async () => {
    if (!job?.id) {
      toast.error("No job id");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/timing-services/apply/${job.id}`, {
        service_type: serviceType,
        template_id: templateId,
        cover_type: coverType,
        selected_op_ids: Array.from(selected).filter(
          (id) => !resolvedPack.mandatory_ops.some((m) => m.id === id)
        ),
        labour_hours_override: labourOverride ? Number(labourOverride) : null,
      });
      toast.success(`Applied ${resolvedPack.name} to estimate`);
      onApplied?.(data.job_group);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "integrity_blocker") {
        toast.error(`Blocked: ${detail.blockers?.[0]?.message || "Missing mandatory op"}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to apply pack");
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleOp = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="timing-service-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-indigo-600" />
            Add Timing Service
            <span className="ml-2 text-xs uppercase tracking-wider text-slate-500">
              Step {step} of 4
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* ====== STEP 1 — Service type ====== */}
        {step === 1 && (
          <div className="space-y-4" data-testid="timing-step-1">
            <p className="text-sm text-slate-600">
              Choose the timing service to estimate. Each template includes mandatory parts, recommended add-ons,
              workshop risks and customer wording — built like a senior workshop controller would.
            </p>
            {Object.entries(SERVICE_LABELS).map(([svc, meta]) => {
              const Icon = meta.icon;
              const items = templatesByService[svc] || [];
              if (items.length === 0) return null;
              return (
                <div key={svc} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-4 h-4 ${meta.color}`} />
                    <h3 className="font-semibold text-slate-800">{meta.label}</h3>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {items.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => goStep2(t.id)}
                        className="text-left p-3 rounded border border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                        data-testid={`timing-tpl-${t.id}`}
                      >
                        <div className="font-medium text-slate-800 flex items-center justify-between">
                          {t.name}
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Baseline {t.default_labour_hours}h · {coverTypes.find((c) => c.id === t.default_cover_type)?.label || t.default_cover_type}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ====== STEP 2 — Vehicle intelligence ====== */}
        {step === 2 && (
          <div className="space-y-4" data-testid="timing-step-2">
            <p className="text-sm text-slate-600">
              Verify vehicle data — engine code lets us layer engine-specific intelligence
              (Ford EcoBoost / PSA PureTech / BMW N47 etc).
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Manufacturer</Label>
                <Input value={manufacturer} onChange={(e) => setManufacturer(e.target.value)}
                  placeholder="e.g. Ford" data-testid="timing-mfr-input" />
              </div>
              <div>
                <Label>Engine Code {!engineCode && <span className="text-xs text-amber-600 ml-1">(missing — please type)</span>}</Label>
                <Input value={engineCode} onChange={(e) => setEngineCode(e.target.value)}
                  placeholder="e.g. SFCB" data-testid="timing-engine-code-input" />
              </div>
              <div>
                <Label>Displacement (L)</Label>
                <Input type="number" step="0.1" value={displacement} onChange={(e) => setDisplacement(e.target.value)}
                  placeholder="1.0" data-testid="timing-displacement-input" />
              </div>
              <div>
                <Label>Cover Type</Label>
                <select className="w-full border border-slate-200 rounded px-2 py-2 text-sm"
                  value={coverType || ""} onChange={(e) => setCoverType(e.target.value)}
                  data-testid="timing-cover-select">
                  {coverTypes.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={goStep3} disabled={busy} data-testid="timing-step-2-next">
                Build Pack →
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ====== STEP 3 — Build pack (mandatory + recommended + risks) ====== */}
        {step === 3 && resolvedPack && (
          <div className="space-y-4" data-testid="timing-step-3">
            {resolvedPack.engine_intelligence && (
              <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-sm">
                <strong>{resolvedPack.engine_intelligence.name}</strong> intelligence applied —
                additional risks + labour uplift to {resolvedPack.labour_uplift_pct}%.
              </div>
            )}

            <Section title="Mandatory operations" subtle="Always included">
              {(resolvedPack.mandatory_ops || []).map((op) => (
                <OpRow key={op.id} op={op} checked locked
                  testid={`timing-op-mandatory-${op.id}`} />
              ))}
            </Section>

            <Section title="Recommended operations" subtle="Pre-ticked — untick to exclude">
              {(resolvedPack.recommended_ops || []).map((op) => (
                <OpRow key={op.id} op={op}
                  checked={selected.has(op.id)} onToggle={() => toggleOp(op.id)}
                  reason={op.reason}
                  testid={`timing-op-rec-${op.id}`} />
              ))}
              {(resolvedPack.recommended_ops || []).length === 0 && (
                <p className="text-xs text-slate-500 italic">No recommended operations.</p>
              )}
            </Section>

            <Section title="Conditional operations" subtle="Add if inspection confirms">
              {(resolvedPack.conditional_ops || []).map((op) => (
                <OpRow key={op.id} op={op}
                  checked={selected.has(op.id)} onToggle={() => toggleOp(op.id)}
                  trigger={op.trigger}
                  testid={`timing-op-cond-${op.id}`} />
              ))}
              {(resolvedPack.conditional_ops || []).length === 0 && (
                <p className="text-xs text-slate-500 italic">None.</p>
              )}
            </Section>

            <Section title="Potential additional work risks" subtle="NOT priced — workshop awareness">
              {(resolvedPack.risks || []).map((r) => (
                <div key={r.id} className="border rounded p-2.5 mb-2"
                  data-testid={`timing-risk-${r.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldAlert className="w-4 h-4 text-amber-600" />
                    <span className="font-semibold text-sm">{r.name}</span>
                    <Badge variant="outline" className={`ml-auto ${RISK_LEVEL_COLORS[r.level]}`}>
                      {r.level}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600">{r.description}</p>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                    {r.customer_visible ? "Visible to customer · " : ""}
                    {r.auth_required ? "Auth required" : "Auto-proceed"}
                  </div>
                </div>
              ))}
            </Section>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={goStep4} disabled={busy} data-testid="timing-step-3-next">
                Review & Apply →
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ====== STEP 4 — Review & apply ====== */}
        {step === 4 && resolvedPack && (
          <div className="space-y-4" data-testid="timing-step-4">
            <div className="grid grid-cols-3 gap-2">
              <ConfidenceCard label="Labour confidence" value={resolvedPack.labour_confidence} />
              <ConfidenceCard label="Parts confidence" value={resolvedPack.parts_confidence} />
              <ConfidenceCard label="Additional cost risk" value={resolvedPack.additional_cost_risk} invert />
            </div>

            <div>
              <Label>Labour time (hours)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" step="0.25" min="0" max="40"
                  value={labourOverride}
                  placeholder={`${(resolvedPack.default_labour_hours * (1 + resolvedPack.labour_uplift_pct / 100)).toFixed(2)} (uplift applied)`}
                  onChange={(e) => setLabourOverride(e.target.value)}
                  className="w-40" data-testid="timing-labour-override-input" />
                <span className="text-xs text-slate-500">
                  Baseline {resolvedPack.default_labour_hours}h + {resolvedPack.labour_uplift_pct}% uplift.
                  Override if needed.
                </span>
              </div>
            </div>

            {integrity?.blockers?.length > 0 && (
              <div className="bg-red-50 border border-red-300 rounded p-3" data-testid="timing-integrity-blockers">
                <div className="flex items-center gap-2 font-semibold text-red-800 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  Cannot apply — mandatory ops missing
                </div>
                {integrity.blockers.map((b, i) => (
                  <p key={i} className="text-sm text-red-700">• {b.message}</p>
                ))}
              </div>
            )}

            {integrity?.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded p-3" data-testid="timing-integrity-warnings">
                <div className="flex items-center gap-2 font-semibold text-amber-800 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  Recommended items missing
                </div>
                {integrity.warnings.map((w, i) => (
                  <p key={i} className="text-sm text-amber-700">• {w.message}</p>
                ))}
              </div>
            )}

            {resolvedPack.customer_warning && (
              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <div className="flex items-center gap-2 font-semibold text-slate-700 text-sm mb-1">
                  <CheckCircle2 className="w-4 h-4 text-slate-500" />
                  Customer wording (sent on portal)
                </div>
                <p className="text-xs text-slate-600 italic">"{resolvedPack.customer_warning}"</p>
              </div>
            )}

            {resolvedPack.technician_notes?.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <div className="font-semibold text-blue-800 text-sm mb-1">Technician notes</div>
                <ul className="text-xs text-blue-700 list-disc list-inside space-y-0.5">
                  {resolvedPack.technician_notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
              <Button onClick={apply} disabled={busy || integrity?.blockers?.length > 0}
                data-testid="timing-apply-btn"
                className="bg-indigo-600 hover:bg-indigo-700">
                Apply to estimate
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, subtle, children }) {
  return (
    <div>
      <h4 className="font-semibold text-slate-800 text-sm mb-2">
        {title}
        {subtle && <span className="ml-2 text-xs font-normal text-slate-500">{subtle}</span>}
      </h4>
      <div>{children}</div>
    </div>
  );
}

function OpRow({ op, checked, onToggle, locked, reason, trigger, testid }) {
  return (
    <div className={`flex items-start gap-2 p-2 rounded ${locked ? "bg-slate-50" : "hover:bg-slate-50"}`}
      data-testid={testid}>
      <Checkbox checked={checked} disabled={locked}
        onCheckedChange={() => !locked && onToggle?.()} />
      <div className="flex-1 min-w-0">
        <div className="text-sm flex items-center gap-2">
          <span className={locked ? "font-semibold text-slate-800" : "text-slate-700"}>{op.label}</span>
          <Badge variant="outline" className="text-[10px]">{op.category}</Badge>
          {locked && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">Mandatory</Badge>}
        </div>
        {reason && <p className="text-xs text-slate-500 mt-0.5">{reason}</p>}
        {trigger && <p className="text-xs text-amber-600 mt-0.5">Trigger: {trigger}</p>}
      </div>
    </div>
  );
}

function ConfidenceCard({ label, value, invert }) {
  // For "additional cost risk" we invert the colour scheme (low risk = good).
  const k = invert
    ? (value === "low" ? "high" : value === "high" ? "low" : "medium")
    : value;
  return (
    <div className={`border rounded p-2 ${CONFIDENCE_COLORS[k] || ""}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-bold text-sm capitalize">{value}</div>
    </div>
  );
}
